import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Boxes, ChevronLeft, ChevronRight, ShieldCheck, Globe2 } from "lucide-react";
import { useRFQ } from "../../context/RFQContext";
import { useContent } from "../../context/ContentContext";
import { imageSized } from "../../api";
import { Link } from "react-router-dom";

export default function Hero() {
  const { openRFQ } = useRFQ();
  const { content } = useContent();
  const { company, hero, stats } = content;

  // Hero figures come from Admin → Site Content → Stats (order: partners, products, categories, countries).
  const stat = (i) => (stats && stats[i]) || { value: "", label: "" };

  // Headline = Company tagline (admin → Company). The optional "highlighted phrase" is shown in teal italic.
  const tagline = (company.tagline || "").trim();
  const hl = (hero.titleHighlight || "").trim();
  let headline;
  if (!tagline) {
    headline = (
      <>
        {hero.titleLead} <span className="text-[#2ED9B8] italic">{hl}</span> {hero.titleTail}
      </>
    );
  } else if (hl && tagline.includes(hl)) {
    const [before, ...rest] = tagline.split(hl);
    headline = (
      <>
        {before}
        <span className="text-[#2ED9B8] italic">{hl}</span>
        {rest.join(hl)}
      </>
    );
  } else {
    headline = tagline;
  }

  // ---- Slider ----
  const slides = (hero.slides || []).filter((s) => s && s.image);
  const count = slides.length;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const cur = count ? active % count : 0;
  const interval = Math.max(2, Number(hero.interval) || 5) * 1000;
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const go = useCallback((i) => setActive(count ? (i + count) % count : 0), [count]);

  // Auto-advance; the timer restarts after any manual change and pauses on hover/focus/touch.
  useEffect(() => {
    if (count < 2 || paused || reduceMotion) return undefined;
    const t = setTimeout(() => go(cur + 1), interval);
    return () => clearTimeout(t);
  }, [cur, count, paused, interval, go, reduceMotion]);

  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) go(cur + (dx < 0 ? 1 : -1));
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") go(cur + 1);
    if (e.key === "ArrowLeft") go(cur - 1);
  };

  return (
    <section className="relative mint-gradient pt-28 pb-12 lg:pt-32 lg:pb-16">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div
          role="region"
          aria-roledescription="carousel"
          aria-label="Featured"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setPaused(true); }}
          onTouchEnd={(e) => { onTouchEnd(e); setPaused(false); }}
          className="relative flex items-center min-h-[600px] lg:min-h-[640px] rounded-[28px] overflow-hidden bg-[#06111d] ring-1 ring-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2ED9B8]"
        >
          {/* Slides (cross-fade) */}
          <div className="absolute inset-0" aria-live={paused ? "polite" : "off"}>
            {slides.map((s, i) => (
              <img
                key={`${s.image}-${i}`}
                src={imageSized(s.image, 1920)}
                alt={i === cur ? s.alt || "" : ""}
                aria-hidden={i !== cur}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${i === cur ? "opacity-100" : "opacity-0"}`}
              />
            ))}
          </div>
          {/* Readability overlays */}
          {/* Desktop: fade from the left where the text sits. Phone: lighter top-to-bottom wash so the photo stays visible. */}
          <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-[#06111d]/95 via-[#06111d]/70 to-[#06111d]/20" />
          <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-[#06111d]/80 via-[#06111d]/62 to-[#06111d]/50" />

          {/* Fixed hero copy */}
          <div className="relative z-10 w-full max-w-3xl px-6 sm:px-20 lg:px-28 pt-14 pb-28">
            <h1 className="font-display text-[34px] leading-[1.1] sm:text-[42px] lg:text-[48px] text-foreground tracking-tight">
              {headline}
            </h1>

            <p className="mt-6 text-[16.5px] leading-relaxed text-white/80 max-w-xl">
              {company.intro}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={openRFQ}
                className="group inline-flex items-center gap-2 bg-[#d9a441] hover:bg-[#c8942f] text-[#0c1626] rounded-full h-13 px-7 py-3.5 font-semibold transition-colors shadow-[0_12px_28px_-10px_rgba(217,164,65,0.7)]"
              >
                Request an RFQ
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-foreground border border-white/20 rounded-full h-13 px-7 py-3.5 font-medium transition-colors"
              >
                Explore Products
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/75">
              <span className="inline-flex items-center gap-2"><Globe2 className="h-4 w-4 text-[#2ED9B8]" /> {stat(3).value} Countries</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2ED9B8]" /> Regulatory-ready</span>
              <span className="inline-flex items-center gap-2"><Boxes className="h-4 w-4 text-[#2ED9B8]" /> {stat(1).value} Products</span>
            </div>
          </div>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(cur - 1)}
                aria-label="Previous slide"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-12 w-12 rounded-full bg-[#06111d]/60 hover:bg-[#00ab96] border border-white/25 text-white hidden sm:flex items-center justify-center backdrop-blur transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => go(cur + 1)}
                aria-label="Next slide"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-12 w-12 rounded-full bg-[#06111d]/60 hover:bg-[#00ab96] border border-white/25 text-white hidden sm:flex items-center justify-center backdrop-blur transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              {/* Thumbnails */}
              <div className="absolute bottom-5 inset-x-0 z-20 flex items-center justify-center gap-3 px-4">
                {slides.map((s, i) => (
                  <button
                    key={`thumb-${s.image}-${i}`}
                    type="button"
                    onClick={() => go(i)}
                    aria-label={`Show slide ${i + 1} of ${count}`}
                    aria-current={i === cur}
                    className={`h-11 w-11 sm:h-12 sm:w-12 rounded-full overflow-hidden border-2 transition-all duration-300 ${
                      i === cur ? "border-[#2ED9B8] scale-110 opacity-100" : "border-white/30 opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={imageSized(s.image, 160)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
